import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useGoogleTokenMutation } from '../../../core/api/authSlice';
import { useCheckout, useCheckoutHelper, useScript } from '../../../helpers/customHooks';
import { AuthSettings } from '../../../settings/appSettings';
import { PagesRoutes } from '../../../store/routes';
import { useSelector } from '../../../configureStore';
import { CheckoutType } from '../../../store/checkout/types';

export const GoogleLogin = () => {
  const navigate = useNavigate();
  const checkout = useSelector((state) => state.checkout);
  const { checkoutCancel } = useCheckout();
  const { isProcessed, isCompleted } = useCheckoutHelper();

  const checkoutFlowRef = useRef({
    checkout,
    checkoutCancel,
    isProcessed,
    isCompleted,
  });
  checkoutFlowRef.current = { checkout, checkoutCancel, isProcessed, isCompleted };

  const [googleUser, setGoogleUser] = useState<GoogleUserType | null>(null);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const [googleLogin] = useGoogleTokenMutation();

  const initGoogle = useCallback(() => {
    const el = googleButtonRef.current;
    if (!el || !AuthSettings.GOOGLE_CLIENT_ID) {
      return;
    }
    window.google.accounts.id.initialize({
      client_id: AuthSettings.GOOGLE_CLIENT_ID,
      callback: (user: GoogleUserType) => {
        setGoogleUser(user);
      },
      auto_select: false,
    });
    window.google.accounts.id.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      shape: 'pill',
      locale: 'en',
      width: 302,
      text: 'continue_with',
      logo_alignment: 'center',
    });
  }, []);

  useScript(AuthSettings.GOOGLE_SDK, initGoogle);

  useEffect(() => {
    const credential = googleUser?.credential;
    if (!credential) {
      return;
    }

    const body = {
      access_token: credential,
      client_id: AuthSettings.CLIENT_ID,
    };

    let cancelled = false;

    (async () => {
      try {
        await googleLogin(body).unwrap();
        if (cancelled) {
          return;
        }
        const { checkout: ch, checkoutCancel: cancel, isProcessed: processed, isCompleted: completed } =
          checkoutFlowRef.current;
        if (processed(ch) && !completed(ch)) {
          await cancel(ch as CheckoutType);
        }
        navigate(PagesRoutes.Profile, { replace: true });
      } catch {
        if (!cancelled) {
          navigate(PagesRoutes.Login, { replace: true });
        }
      } finally {
        if (!cancelled) {
          setGoogleUser(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [googleUser?.credential, googleLogin, navigate]);

  return (
    <Container>
      <div ref={googleButtonRef} />
    </Container>
  );
};

const Container = styled.div`
  width: auto;
  height: 44px;
`;

type GoogleUserType = {
  clientId?: string;
  credential: string;
};
